import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Badge, Button, Group, Stack, Drawer, Anchor, Checkbox,
  ActionIcon, Tooltip, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconEye, IconRefresh, IconPlus, IconAdjustments, IconListDetails,
} from '@tabler/icons-react';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, StatusKPICard, StatusKPISkeleton, getStatusIcon, ListPagination } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { sortRows } from '@/utils/tableSort';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService, useACCourseService, useRegistrationStatusService,
  useFacilitatorService, useLocationService, useACCourseSettingService,
  RegistrationListView as RegistrationRow, RegistrationStatus, RegistrationFilterModel,
  useRegistrationStreaming,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import BulkDeleteControls from '@/components/common/BulkDeleteControls';
import RowDeleteAction from '@/components/common/RowDeleteAction';
import ExportCsvButton from '@/components/common/ExportCsvButton';
import { useBulkDelete } from '@/hooks/useBulkDelete';
import { useModuleDeleteSettings } from '@/hooks/useModuleDeleteSettings';
import { AccessControl } from '@/components';

const PAGE_SIZE = 10;

/**
 * Statuses that get a KPI card, in display order (an "All" card is prepended).
 *
 * Matched on name because registration status IDs are assigned per site rather than fixed by the
 * backend contract, so renaming one of these in /LookUp will drop its card.
 */
const KPI_STATUS_NAMES = ['pending', 'active', 'completed'];

/** Cards actually rendered: the statuses above plus "All". Drives the grid width and the skeletons. */
const KPI_CARD_COUNT = KPI_STATUS_NAMES.length + 1;

interface RegistrationsListViewProps {
  /** Locks the page to a single statusID (e.g. 0 = Pending, 1 = Active, -1 = Cancelled).
   *  When set, the KPI cards are hidden and the drawer's Status field is pinned. */
  fixedStatusID?: number;
  title?: string;
  subtitle?: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function RegistrationsListView({ fixedStatusID, title, subtitle }: RegistrationsListViewProps) {
  const navigate            = useNavigate();
  const registrationService = useRegistrationService();
  const courseService       = useACCourseService();
  const statusService       = useRegistrationStatusService();
  const facilitatorService  = useFacilitatorService();
  const locationService     = useLocationService();
  const settingService      = useACCourseSettingService();
  const deleteSettings = useModuleDeleteSettings(settingService, 'Registrations');
  const bulk = useBulkDelete(registrationService, deleteSettings.allowBulkDelete, 'registration');
  const { refetch } = useSidebarCountsContext();

  const showStatusKPIs = fixedStatusID === undefined;

  // ── Table / pagination ──────────────────────────────────────────────────
  const [activePage, setActivePage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [pageSize, setPageSize]     = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');

  // ── Filter draft state ──────────────────────────────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState(
    fixedStatusID !== undefined ? String(fixedStatusID) : '',
  );
  const [filterCourseID, setFilterCourseID] = useState('');
  const [filterDateID, setFilterDateID] = useState('');
  const [filterFacilitatorUserId, setFilterFacilitatorUserId] = useState('');
  const [filterLocationID, setFilterLocationID] = useState('');
  /**
   * Location is applied **in memory**, separately from `appliedFilter`.
   *
   * ⚠️ `appliedFilter` is handed to `useRegistrationStreaming`, which sends it to the server, and
   * `RegistrationFilterModel` has **no `locationID`**. Putting it in there would ship a field the API
   * does not accept. `RegistrationListView` does carry `locationID`, so filtering the streamed rows is
   * exact — and this page already streams every row and searches them client-side anyway.
   */
  const [appliedLocationID, setAppliedLocationID] = useState('');

  const [appliedFilter, setAppliedFilter] = useState<Partial<RegistrationFilterModel>>(
    fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {},
  );

  // The pinned status on a status-locked page is not a user-chosen filter, so it does not count
  // towards "filters are active".
  const isFilterActive = Boolean(
    (showStatusKPIs && appliedFilter.statusID) || appliedFilter.courseID ||
    appliedFilter.dateID || appliedFilter.facilitatorUserId || appliedLocationID,
  );

  // ── Lookup data ─────────────────────────────────────────────────────────
  const [courses, setCourses] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<RegistrationStatus[]>([]);
  const [facilitators, setFacilitators] = useState<any[]>([]);
  const [locations, setLocations] = useState<{ locationID: number; name: string }[]>([]);
  const [statusesLoading, setStatusesLoading] = useState(true);
  // Intake dates belong to a course, so this list only fills once a course is picked.
  const [courseDates, setCourseDates] = useState<any[]>([]);

  // ── Confirm delete ──────────────────────────────────────────────────────

  // Keep filterStatus pinned if this instance is locked to a status
  useEffect(() => {
    if (fixedStatusID !== undefined) setFilterStatus(String(fixedStatusID));
  }, [fixedStatusID]);

  const visibleStatuses = useMemo(
    () =>
      statuses
        .filter((s) => KPI_STATUS_NAMES.includes((s.name ?? '').trim().toLowerCase()))
        .sort(
          (a, b) =>
            KPI_STATUS_NAMES.indexOf((a.name ?? '').trim().toLowerCase()) -
            KPI_STATUS_NAMES.indexOf((b.name ?? '').trim().toLowerCase()),
        ),
    [statuses],
  );

  // ── Stream registrations ────────────────────────────────────────────────
  // Streamed in 50-row batches so rows paint as they arrive. Drawer filters go server-side through
  // the stream filter; `search` stays client-side because re-streaming on every keystroke would be
  // wasteful and the full filtered set is already in memory.
  const {
    items: registrations,
    isLoading: loading,
    progress,
    loadedCount,
    load: fetchAll,
  } = useRegistrationStreaming(appliedFilter, 50);

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    fetchAll().then(() => refetchRef.current());
  }, [fetchAll]);

  useEffect(() => { setActivePage(1); }, [searchTerm, appliedFilter, appliedLocationID]);

  // ── Fetch lookups ───────────────────────────────────────────────────────
  const fetchLookups = useCallback(async () => {
    try {
      const [c, f, l] = await Promise.all([
        courseService.getAll(),
        facilitatorService.list(),
        locationService.getAll(),
      ]);
      setCourses((c as any)?.data ?? c ?? []);
      setFacilitators((f as any)?.data ?? f ?? []);
      setLocations((Array.isArray(l) ? l : []).filter((x) => x.isActive));
    } catch (e) {
      console.error('Error fetching registration lookups:', e);
    }
    setStatusesLoading(true);
    try {
      const s = await statusService.getAll();
      setStatuses((s as any)?.data ?? s ?? []);
    } catch (e) {
      console.error('Error fetching statuses:', e);
    } finally {
      setStatusesLoading(false);
    }
  }, [courseService, facilitatorService, locationService, statusService]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  // Course dates cascade off the selected course, mirroring the registration wizard.
  useEffect(() => {
    if (!filterCourseID) {
      setCourseDates([]);
      return;
    }
    courseService
      .getDates(Number(filterCourseID))
      .then((d) => setCourseDates((d as any)?.data ?? d ?? []))
      .catch((e) => console.error('Error fetching course dates:', e));
  }, [filterCourseID, courseService]);

  // ── Global per-status counts for the KPI cards ──────────────────────────
  // Deliberately independent of the applied filter, so selecting a status does not collapse
  // every other card to zero.
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [allCount, setAllCount] = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);

  const fetchStatusCounts = useCallback(async () => {
    if (!showStatusKPIs || statuses.length === 0) return;
    setCountsLoading(true);
    try {
      const [allRes, ...perStatusRes] = await Promise.all([
        registrationService.listView({ take: 1, skip: 0 } as any),
        ...visibleStatuses.map((s) =>
          registrationService.listView({ take: 1, skip: 0, statusID: String(s.statusID) } as any),
        ),
      ]);
      setAllCount((allRes as any)?.totalCount ?? 0);
      const counts: Record<string, number> = {};
      visibleStatuses.forEach((s, i) => {
        counts[String(s.statusID)] = (perStatusRes[i] as any)?.totalCount ?? 0;
      });
      setStatusCounts(counts);
    } catch (e) {
      console.error('Error fetching registration status counts:', e);
    } finally {
      setCountsLoading(false);
    }
  }, [showStatusKPIs, statuses, visibleStatuses, registrationService]);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  // ── Filter handlers ─────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setAppliedFilter({
      statusID:          fixedStatusID !== undefined ? String(fixedStatusID) : filterStatus,
      courseID:          filterCourseID,
      dateID:            filterDateID,
      facilitatorUserId: filterFacilitatorUserId,
    });
    setAppliedLocationID(filterLocationID);
    setActivePage(1);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setFilterStatus(fixedStatusID !== undefined ? String(fixedStatusID) : '');
    setFilterCourseID('');
    setFilterDateID('');
    setFilterFacilitatorUserId('');
    setFilterLocationID('');
    setAppliedFilter(fixedStatusID !== undefined ? { statusID: String(fixedStatusID) } : {});
    setAppliedLocationID('');
    setActivePage(1);
  };

  // ── Client-side location filter + search over the streamed set ──────────
  const filtered = useMemo(() => {
    const byLocation = appliedLocationID
      ? registrations.filter((r) => String(r.locationID ?? '') === String(appliedLocationID))
      : registrations;

    const term = searchTerm.trim().toLowerCase();
    if (!term) return byLocation;
    return byLocation.filter((r) =>
      [r.reference, r.description, r.courseName, r.locationName, r.leadFacilitatorName, r.statusName]
        .some((field) => (field ?? '').toLowerCase().includes(term))
      || String(r.registrationID ?? '').includes(term),
    );
  }, [registrations, appliedLocationID, searchTerm]);


  // ── Delete ──────────────────────────────────────────────────────────────
  /**
   * ⚠️ Unlike the other three queues, this delete **already worked** before the settings gate existed.
   * Defaulting to off would therefore have removed a live capability the day this shipped, so an absent
   * `AllowDelete` row leaves it as it was and only an explicit `false` turns it off. The queues whose
   * delete is new stay closed until switched on.
   */
  const rowDeleteAllowed = !deleteSettings.deleteConfigured || deleteSettings.allowDelete;

  // ⚠️ Deliberately does **not** catch — `RowDeleteAction` words the failure and keeps its dialog open.
  const deleteOne = async (registrationID: number) => {
    await registrationService.delete(registrationID);
    await fetchAll();
    toast.success('Deleted', 'The registration was removed.');
  };

  const facilitatorLabel = (f: any) =>
    [f.firstName, f.surname].filter(Boolean).join(' ').trim()
    || f.fullName?.trim()
    || f.displayName?.trim()
    || f.userId;

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns: TableColumn<RegistrationRow>[] = [
    ...(bulk.selecting ? [{
      key: 'select',
      header: '',
      accessor: (r: RegistrationRow) => r.registrationID,
      sortable: false,
      width: 44,
      render: (_: unknown, r: RegistrationRow) => (
        <Checkbox
          checked={bulk.isSelected(r.registrationID)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => bulk.toggle(r.registrationID)}
        />
      ),
    } as TableColumn<RegistrationRow>] : []),
    { key: 'registrationID', header: 'ID', accessor: (r) => r.registrationID, sortable: true, width: 90 },
    { key: 'description', header: 'Description', accessor: (r) => r.description ?? '—', sortable: true },
    { key: 'Date', header: 'Date', accessor: (r) => r.periodDisplay ?? '—', sortable: true },
    {
      // Pre-formatted "d MMM yyyy" from the API, so it is printed as-is rather than run through
      // formatSDKDate, which expects yyyyMMddHHmmss.
      key: 'registeredDate', header: 'Registered', accessor: (r) => r.registeredDate ?? '—', sortable: true,
    },
    {
      key: 'courseID',
      header: 'Course',
      accessor: (r) => r.courseName ?? `Course ${r.courseID}`,
      sortable: true,
      render: (value, r) =>
        r.courseName ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.courseColor)}>
            {value}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">{value as string}</Text>
        ),
    },
    ...(showStatusKPIs
      ? [{
          key: 'statusID',
          header: 'Status',
          accessor: (r: RegistrationRow) => r.statusName ?? String(r.statusID ?? '—'),
          sortable: true,
          align: 'center' as const,
          render: (value: unknown, r: RegistrationRow) => (
            <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.statusColor)}>
              {value as string}
            </Badge>
          ),
        }]
      : []),
    {
      key: 'locationName', header: 'Location', accessor: (r) => r.locationName ?? '—', sortable: true,
      render: (value, r) => (r.locationName
        ? (
          <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.locationColor)}>
            {value}
          </Badge>
        )
        : '—'),
    },
    {
      key: 'Facilitators', header: 'Facilitator',
      accessor: (r) => r.leadFacilitatorName ?? '—', sortable: true,
      render: (value, r) => (
        // `leadFacilitatorUserId` (SDK 0.0.50) makes the name clickable. Before it, the row carried a
        // name and no id, so two same-named facilitators were indistinguishable and neither linked.
        r.leadFacilitatorUserId
          ? (
            <Anchor
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/Information/facilitators/${r.leadFacilitatorUserId}`);
              }}
            >
              {value as string}
            </Anchor>
          )
          : <Text size="sm">{(value as string) || '—'}</Text>
      ),
    },
    { key: 'Sessions', header: 'Sessions', accessor: (r) => r.sessionCount ?? '—', sortable: true, align: 'center' },
    {
      key: 'actions',
      header: '',
      accessor: (r) => r,
      sortable: false,
      align: 'center',
      width: 110,
      render: (_, row) => (
        <Group gap={6} justify="center" wrap="nowrap">
          <Tooltip label="View">
            <ActionIcon variant="light" color="blue" size="sm"
              onClick={(e) => { e.stopPropagation(); navigate(`/Grow/Registration/RegistrationDetails/${row.registrationID}`); }}>
              <IconEye size={14} />
            </ActionIcon>
          </Tooltip>
          <AccessControl roles={['Admin']} mode='hide'>
          <RowDeleteAction
            id={row.registrationID}
            entityLabel="registration"
            recoverable
            recordLabel={row.reference || `registration #${row.registrationID}`}
            confirmWord={deleteSettings.confirmWord}
            allowed={rowDeleteAllowed}
            supported
            onDelete={deleteOne}
          />
          </AccessControl>
        </Group>
      ),
    },
  ];

  /**
   * ⚠️ Sorting lives here, not in `CCMSDataTable`. The table only draws the arrow and calls `onSort`;
   * this page previously threw that away, so the arrows moved and the rows never did.
   * `sortRows` keys off each column's own `accessor`, so it cannot disagree with the cell.
   *
   * Declared **after** `columns` on purpose: `useMemo` runs during render, so referencing `columns`
   * from above it would hit the temporal dead zone.
   */
  const sorted = useMemo(
    () => sortRows(filtered, columns, sortConfig),
    [filtered, columns, sortConfig],
  );

  const paginated = useMemo(
    () => sorted.slice((activePage - 1) * pageSize, activePage * pageSize),
    [sorted, activePage, pageSize],
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      {/* Header: title/subtitle left, Refresh far right, matching /Counselling/Cases */}
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>{title ?? 'Registrations'}</Title>
          <Text c="dimmed">{subtitle ?? 'Manage course registrations.'}</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={() => fetchAll()} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Status KPI cards (All Registrations page only) */}
      {showStatusKPIs && (
        <SimpleGrid cols={{ base: 2, sm: 3, lg: KPI_CARD_COUNT }} spacing="md" mb="md">
          {(countsLoading || statusesLoading) ? (
            Array.from({ length: KPI_CARD_COUNT }).map((_, i) => <StatusKPISkeleton key={i} />)
          ) : (
            <>
              <StatusKPICard
                icon={IconListDetails}
                label="All"
                count={allCount}
                color="#4dabf7"
                active={filterStatus === ''}
                onClick={() => {
                  setFilterStatus('');
                  setAppliedFilter((f) => { const { statusID, ...rest } = f; return rest; });
                  setActivePage(1);
                }}
              />
              {visibleStatuses.map((s) => (
                <StatusKPICard
                  key={s.statusID}
                  icon={getStatusIcon(s.icon)}
                  label={s.name}
                  count={statusCounts[String(s.statusID)] ?? 0}
                  color={s.color || '#868e96'}
                  active={filterStatus === String(s.statusID)}
                  onClick={() => {
                    const next = filterStatus === String(s.statusID) ? '' : String(s.statusID);
                    setFilterStatus(next);
                    setAppliedFilter((f) => ({ ...f, statusID: next || undefined }));
                    setActivePage(1);
                  }}
                />
              ))}
            </>
          )}
        </SimpleGrid>
      )}

      {/* Search & filter bar */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by reference, description, course, or facilitator…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '320px' } }}
              size="sm"
            />
            <Button variant="outline" leftSection={<IconAdjustments size={16} />} onClick={() => setFiltersOpen(true)} disabled={loading}>
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <Flex gap="sm" align="center" wrap="wrap">
            <AccessControl roles={['Admin']} mode='hide'>
            <BulkDeleteControls
              bulk={bulk}
              entityLabel="registrations"
              confirmWord={deleteSettings.confirmWord}
              allowed={deleteSettings.allowBulkDelete}
              recoverable
            onDeleted={async ({ removed, requested, failure }) => {
                await fetchAll();
                if (failure) return;
                if (removed === 0) {
                  toast.warning('Nothing deleted', 'No registrations were removed.');
                } else if (removed < requested) {
                  toast.warning('Some were skipped', `${removed} of ${requested} removed.`);
                } else {
                  toast.success('Deleted', `${removed} registration${removed === 1 ? '' : 's'} removed.`);
                }
              }}
            />
            </AccessControl>
            <ExportCsvButton
              data={sorted}
              columns={columns}
              filename="registrations"
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/Grow/Registration/Form/CreateRegistrationWizard')}
            >
              New Registration
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* Filter drawer */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filter Registrations"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          {/* Status is pinned on the status-locked pages, so it is only offered here */}
          {showStatusKPIs && (
            <Select
              label="Status"
              placeholder="All Statuses"
              data={[
                { value: '', label: 'All Statuses' },
                ...statuses.map((s) => ({ value: String(s.statusID), label: s.name })),
              ]}
              value={filterStatus}
              onChange={(v) => setFilterStatus(v ?? '')}
              clearable
            />
          )}

          <Select
            label="Course"
            placeholder="All Courses"
            data={courses.map((c) => ({
              value: String(c.courseID),
              label: c.name ?? c.courseName ?? `Course ${c.courseID}`,
            }))}
            value={filterCourseID}
            onChange={(v) => {
              setFilterCourseID(v ?? '');
              // A date belongs to a course, so changing the course invalidates the chosen date.
              setFilterDateID('');
            }}
            searchable
            clearable
          />

          <Select
            label="Course Date"
            placeholder={filterCourseID ? 'All Dates' : 'Pick a course first'}
            description={filterCourseID ? undefined : 'Intake dates belong to a course.'}
            disabled={!filterCourseID}
            data={courseDates.map((d) => ({
              value: String(d.dateID),
              label: d.periodDisplay
                ?? [d.startDateTime, d.endDateTime].filter(Boolean).join(' to ')
                ?? `Date ${d.dateID}`,
            }))}
            value={filterDateID}
            onChange={(v) => setFilterDateID(v ?? '')}
            searchable
            clearable
          />

          <Select
            label="Facilitator"
            placeholder="All Facilitators"
            data={facilitators.map((f) => ({ value: String(f.userId), label: facilitatorLabel(f) }))}
            value={filterFacilitatorUserId}
            onChange={(v) => setFilterFacilitatorUserId(v ?? '')}
            searchable
            clearable
          />

          <Select
            label="Location"
            placeholder="All Locations"
            data={locations.map((l) => ({ value: String(l.locationID), label: l.name }))}
            value={filterLocationID}
            onChange={(v) => setFilterLocationID(v ?? '')}
            searchable
            clearable
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { handleClearFilters(); setFiltersOpen(false); }}>
              Clear
            </Button>
            <Button onClick={handleApplyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Table */}
      <Card withBorder radius="md" padding="lg">
        <StreamProgress
          loading={loading}
          progress={progress}
          loadedCount={loadedCount}
          label="registrations"
        />
        <CCMSDataTable
          data={paginated}
          columns={columns}
          loading={loading && registrations.length === 0}
          sortable
          onSort={(config) => { setSortConfig(config); setActivePage(1); }}
          onRowClick={(r) => navigate(`/Grow/Registration/RegistrationDetails/${r.registrationID}`)}
          striped
          highlightOnHover
        />
        <ListPagination
          page={activePage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onPageChange={setActivePage}
          onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
          showPageSize
        />
      </Card>
    </Container>
  );
}
